import React, { useState, useEffect, useRef, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { getGamesByVendor } from "../../services/gameService";
import { useGameLaunch } from "../../hooks/useGameLaunch";

const VENDOR_LIST = [
  { key: "evolution", name: "Evolution Gaming" },
  { key: "pragmatic", name: "Pragmatic Play" },
  { key: "pragmaticlive", name: "Pragmatic Live" },
  { key: "spribe", name: "Spribe" },
  { key: "ideal", name: "Ideal" },
  { key: "microgaming", name: "Microgaming" },
  { key: "pgsoft", name: "PGSoft" },
  { key: "jili", name: "Jili" },
  { key: "ezugi", name: "Ezugi" },
  { key: "Rich88", name: "Rich 88" },
  { key: "eazygaming", name: "Eazy Gaming" },
  { key: "Playtech", name: "Playtech" },
  { key: "CQ9", name: "CQ9" },
  { key: "km", name: "KM" },
  { key: "Evoplay", name: "Evoplay" },
  { key: "NextSpin", name: "NextSpin" },
  { key: "BNG", name: "BNG" },
  { key: "PgsGaming", name: "Pgs Gaming" },
  { key: "netent", name: "NetEnt" },
  { key: "bgaming", name: "BGaming" },
];

const PER_PAGE = 15;

const GlobalSearch = ({ isOpen, onClose }) => {
  const [searchQuery, setSearchQuery] = useState("");
  const [providerSearch, setProviderSearch] = useState("");
  const [selectedVendors, setSelectedVendors] = useState([]);
  const [results, setResults] = useState([]);
  const [initialResults, setInitialResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [showProviders, setShowProviders] = useState(false);
  const [page, setPage] = useState(1);
  const [hasInteracted, setHasInteracted] = useState(false);
  const dropdownRef = useRef(null);
  const debounceRef = useRef(null);
  const handleGameClick = useGameLaunch();

  // Close provider dropdown on outside click
  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowProviders(false);
      }
    };
    document.addEventListener("mousedown", handler);
    return () => document.removeEventListener("mousedown", handler);
  }, []);

  // Fetch games from API for given vendors (exact old frontend pattern)
  const fetchFromAPI = useCallback(async (vendors, pageNum = 1) => {
    setLoading(true);
    try {
      const fetches = await Promise.all(
        vendors.map((v) => getGamesByVendor(v, pageNum, PER_PAGE))
      );
      const games = fetches.flatMap((r) => r.games || []);
      return games;
    } catch {
      return [];
    } finally {
      setLoading(false);
    }
  }, []);

  // Initial load: fetch evolution games (exact old frontend: initializeDefaultGames)
  useEffect(() => {
    if (!isOpen) return;
    let mounted = true;
    (async () => {
      setLoading(true);
      const games = await fetchFromAPI(["evolution"], 1);
      if (mounted) {
        setInitialResults(games);
        if (!hasInteracted) setResults(games);
        setPage(1);
      }
    })();
    return () => { mounted = false; };
  }, [isOpen, fetchFromAPI]);

  // When vendors change: fetch from API
  const handleVendorToggle = useCallback(async (vendorKey) => {
    setSelectedVendors((prev) => {
      const next = prev.includes(vendorKey)
        ? prev.filter((v) => v !== vendorKey)
        : [...prev, vendorKey];

      // Trigger fetch
      if (next.length === 0) {
        setResults(initialResults);
        setHasInteracted(false);
      } else {
        setHasInteracted(true);
        fetchFromAPI(next, 1).then((games) => {
          setResults(games);
          setPage(1);
        });
      }
      return next;
    });
  }, [fetchFromAPI, initialResults]);

  // Search with debounce — fetch from selected vendors or all default vendors
  const handleSearchChange = (e) => {
    const query = e.target.value;
    setSearchQuery(query);

    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      if (!query.trim()) {
        // Reset to vendor results or initial
        if (selectedVendors.length > 0) {
          const games = await fetchFromAPI(selectedVendors, 1);
          setResults(games);
        } else {
          setResults(initialResults);
          setHasInteracted(false);
        }
        return;
      }

      setHasInteracted(true);
      const vendors = selectedVendors.length > 0
        ? selectedVendors
        : VENDOR_LIST.map((v) => v.key);
      const games = await fetchFromAPI(vendors, 1);
      const q = query.toLowerCase();
      const filtered = games.filter(
        (g) =>
          (g.game_name || g.title || g.name || "").toLowerCase().includes(q) ||
          (g.vendor || "").toLowerCase().includes(q)
      );
      setResults(filtered);
      setPage(1);
    }, 500);
  };

  // Load more with skeleton
  const loadMore = async () => {
    setLoadingMore(true);
    const nextPage = page + 1;
    const vendors = selectedVendors.length > 0
      ? selectedVendors
      : ["evolution"];
    const moreGames = await fetchFromAPI(vendors, nextPage);
    setResults((prev) => [...prev, ...moreGames]);
    setPage(nextPage);
    setLoadingMore(false);
  };

  const clearAllVendors = () => {
    setSelectedVendors([]);
    setResults(initialResults);
    setHasInteracted(false);
  };

  const filteredProviders = VENDOR_LIST.filter((p) =>
    p.name.toLowerCase().includes(providerSearch.toLowerCase())
  );

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      <motion.div
        className="gs-overlay"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        onClick={onClose}
      >
        <motion.div
          className="gs-container"
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.25 }}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Header */}
          <div className="gs-header">
            <div />
            <div className="gs-title">Most Popular Games</div>
            <button className="gs-close" onClick={onClose}>
              <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          {/* Search bar + Provider filter */}
          <div className="gs-search-bar">
            <div className="gs-provider-dropdown" ref={dropdownRef}>
              <button className="gs-provider-btn" onClick={() => setShowProviders(!showProviders)}>
                <span>Providers{selectedVendors.length > 0 ? ` (${selectedVendors.length})` : ""}</span>
                <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>

              {showProviders && (
                <motion.div
                  className="gs-provider-menu"
                  initial={{ opacity: 0, y: -10 }}
                  animate={{ opacity: 1, y: 0 }}
                >
                  <div className="gs-provider-search">
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="#808A94" strokeWidth="2" style={{ position: "absolute", left: 10, top: 10 }}>
                      <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
                    </svg>
                    <input
                      placeholder="Search"
                      value={providerSearch}
                      onChange={(e) => setProviderSearch(e.target.value)}
                    />
                  </div>
                  <div className="gs-provider-list">
                    {filteredProviders.map((p) => (
                      <div key={p.key} className="gs-provider-item" onClick={() => handleVendorToggle(p.key)}>
                        <div className={`gs-checkbox ${selectedVendors.includes(p.key) ? "checked" : ""}`} />
                        <span>{p.name}</span>
                      </div>
                    ))}
                  </div>
                  <button className="gs-clear-btn" onClick={clearAllVendors}>
                    Clear All
                  </button>
                </motion.div>
              )}
            </div>

            <div className="gs-input-wrap">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="#808A94" strokeWidth="2" className="gs-search-icon">
                <circle cx="11" cy="11" r="8" /><line x1="21" y1="21" x2="16.65" y2="16.65" />
              </svg>
              <input
                type="text"
                placeholder="Search games..."
                value={searchQuery}
                onChange={handleSearchChange}
                autoFocus
              />
            </div>
          </div>

          {/* Results */}
          <div className="gs-section-heading">
            {selectedVendors.length > 0
              ? `${selectedVendors.map((v) => VENDOR_LIST.find((vl) => vl.key === v)?.name || v).join(", ")}`
              : "Most Popular Games"}
            {!loading && results.length > 0 && (
              <span className="gs-game-count">{results.length} Games</span>
            )}
          </div>

          {loading && results.length === 0 ? (
            <div className="gs-grid">
              {Array.from({ length: 9 }).map((_, i) => (
                <div key={i} className="gs-skeleton" />
              ))}
            </div>
          ) : results.length > 0 ? (
            <>
              <div className="gs-grid">
                {results.map((game, i) => (
                  <motion.div
                    key={game.game_uid || game.id || i}
                    className="gs-game-card"
                    initial={{ opacity: 0, y: 20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ delay: (i % PER_PAGE) * 0.03, duration: 0.3 }}
                    onClick={() => handleGameClick(game)}
                  >
                    <img
                      src={game.game_icon || game.details_thumbnails_300x300}
                      alt={game.game_name || game.title || game.name}
                      loading={i < 7 ? "eager" : "lazy"}
                      onError={(e) => { e.target.closest(".gs-game-card").style.display = "none"; }}
                    />
                    <div className="gs-game-overlay">
                      <div className="gs-play-btn">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="white"><polygon points="5 3 19 12 5 21 5 3" /></svg>
                      </div>
                    </div>
                  </motion.div>
                ))}
                {/* Skeleton cards while loading more */}
                {loadingMore && Array.from({ length: 6 }).map((_, i) => (
                  <div key={`skel-${i}`} className="gs-skeleton" />
                ))}
              </div>
              {results.length >= PER_PAGE && (
                <button className="gs-load-more" onClick={loadMore} disabled={loadingMore}>
                  {loadingMore ? "Loading..." : "Load More"}
                </button>
              )}
            </>
          ) : (
            <div className="gs-no-results">
              <h3>No games found</h3>
              <p>Select a provider or search for a game</p>
            </div>
          )}
        </motion.div>
      </motion.div>
    </AnimatePresence>
  );
};

export default GlobalSearch;
